<template>
  <FormPage
    title="Прицепы"
    description="Список всех прицепов в системе"
  >
    <template #controls>
      <button
        class="btn btn-primary"
        type="button"
        @click="router.push({ name: 'TrailerCreate' })"
      >+ Создать прицеп</button>
    </template>

    <FormCard>
      <FormGrid :cols="3">
        <FormField label="Общий поиск">
          <input
            v-model.trim="filters.query"
            type="text"
            placeholder="Гос Номер, VIN-номер"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Поиск по номеру">
          <input
            v-model.trim="filters.queryCarNumber"
            type="text"
            placeholder="Номер прицепа"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Поиск по VIN-номеру">
          <input
            v-model.trim="filters.queryVIN"
            type="text"
            placeholder="VIN-номер"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Статус проверки Автокод">
          <select
            v-model="filters.autocodeStatus"
            @change="onFilterEdited"
          >
            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <div class="filters-actions">
          <button class="btn" type="button" @click="onResetFilters">
            Сбросить
          </button>
        </div>
      </FormGrid>
    </FormCard>

    <FormCard>
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="onPageChanged"
      />

      <my-loader v-if="isLoading" />

      <div v-else-if="hasError && !trailers.length" class="state-block error-block">
        Ошибка загрузки списка прицепов. Попробуйте позже.
      </div>

      <div v-else class="content">
        <div v-if="hasError && trailers.length" class="inline-error">
          Не удалось обновить список. Показаны предыдущие данные.
        </div>

        <div v-if="trailers.length" class="table-scroll">
          <table class="trailers-table">
            <thead>
              <tr>
                <th>Номер</th>
                <th>VIN</th>
                <th>Статус проверки Автокод</th>
                <th>Черный список</th>
                <th>Клиентский ЧС</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in trailers"
                :key="row.id"
                class="row-card"
                :class="{ 'row-placeholder': isPlaceholderData }"
                @click="onRowClick(row)"
              >
                <td :title="row.number">{{ row.number || '—' }}</td>
                <td :title="row.vin">{{ row.vin || '—' }}</td>
                <td>
                  <span
                    v-if="securityOf(row)"
                    :class="['status-badge', securityOf(row).avtocod_is_end ? 'st-verified' : 'st-nverified']"
                  >
                    {{ securityOf(row).avtocod_is_end ? 'Проверено' : 'Не проверено' }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td>{{ securityOf(row)?.black_list ? 'Да' : '—' }}</td>
                <td>{{ securityOf(row)?.black_list_client ? 'Да' : '—' }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="isFetching && trailers.length" class="refresh-bar" />
        </div>

        <div v-else class="state-block empty-state">
          Нет прицепов
        </div>
      </div>
    </FormCard>
  </FormPage>
</template>

<script>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'
import { useCarsPage } from './useCarsPage'
import { carSecurityInfo } from './carsState.mjs'
import {
  carsFiltersEqual,
  filtersFromRouteQuery,
  filtersToRouteQuery,
  resolveInitialCarsFilters,
} from './carsState.mjs'

export default {
  name: 'TrailerList',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()

    // presetParams гарантирует, что список всегда фильтруется по прицепам —
    // параметр невозможно сбросить из UI/URL.
    const page = useCarsPage(
      resolveInitialCarsFilters(route.query, window.localStorage),
      { is_trailer: true },
    )
    const {
      filters,
      cars,
      isLoading,
      isFetching,
      isPlaceholderData,
      hasError,
      totalPages,
      currentPage,
      setPage,
      replaceFilters,
      resetFilters,
    } = page

    // Унифицируем имя для шаблона ( внутри это те же cars — модель 'cars' ).
    const trailers = cars

    const statusOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const syncUrl = () => {
      const nextQuery = filtersToRouteQuery(filters)
      const current = route.query || {}
      const same =
        String(current.query || '') === String(nextQuery.query || '')
        && String(current.queryCarNumber || '') === String(nextQuery.queryCarNumber || '')
        && String(current.queryVIN || '') === String(nextQuery.queryVIN || '')
        && String(current.autocodeStatus ?? '') === String(nextQuery.autocodeStatus ?? '')
        && String(current.page || '') === String(nextQuery.page || '')
      if (same) return
      router.replace({ query: nextQuery }).catch(() => {})
    }

    watch(
      () => ({ ...filters }),
      () => { syncUrl() },
      { deep: true },
    )

    let applyRouteFromHistory = false
    const onPopState = () => { applyRouteFromHistory = true }
    onMounted(() => window.addEventListener('popstate', onPopState, true))
    onUnmounted(() => window.removeEventListener('popstate', onPopState, true))

    watch(
      () => route.query,
      (query) => {
        if (!applyRouteFromHistory) return
        applyRouteFromHistory = false
        const fromRoute = filtersFromRouteQuery(query)
        if (carsFiltersEqual(fromRoute, filters)) return
        replaceFilters(fromRoute)
      },
    )

    const onFilterEdited = () => {
      filters.autocodeStatus = Number(filters.autocodeStatus)
      if (filters.page !== 1) filters.page = 1
    }

    const onPageChanged = (nextPage) => { setPage(nextPage) }
    const onResetFilters = () => { resetFilters() }

    const onRowClick = (row) => {
      if (isPlaceholderData.value || !row?.id) return
      router.push({ name: 'TrailerDetail', params: { id: row.id } })
    }

    return {
      router,
      filters,
      trailers,
      isLoading,
      isFetching,
      isPlaceholderData,
      hasError,
      totalPages,
      currentPage,
      statusOptions,
      securityOf: carSecurityInfo,
      onFilterEdited,
      onPageChanged,
      onResetFilters,
      onRowClick,
    }
  },
}
</script>

<style scoped>
.btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
  color: #fff;
}

.filters-actions {
  display: flex;
  align-items: flex-end;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 12px;
  width: 100%;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.error-block {
  color: #c0392b;
}

.empty-state {
  text-align: center;
}

.inline-error {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  background: #fff4e5;
  color: #8a5a00;
  font-size: 13px;
}

.table-scroll {
  width: 100%;
  position: relative;
  overflow-x: auto;
}

.trailers-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
  min-width: 100%;
}

.trailers-table th,
.trailers-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trailers-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: bold;
}

.trailers-table td {
  font-weight: bold;
}

.row-card {
  cursor: pointer;
}

.row-card:hover {
  background-color: rgba(224, 187, 72, 0.4);
}

.row-placeholder {
  opacity: 0.72;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.st-verified {
  background: #e6f4ea;
  color: #1e7e34;
}

.st-nverified {
  background: #fdeaea;
  color: #c0392b;
}

.refresh-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #4f46e5, transparent);
  animation: refresh-slide 1s linear infinite;
}

@keyframes refresh-slide {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}
</style>
